import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Search, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { ShippingProfileApi } from "@/api/shippingProfile.api";
import { DataTable } from "@/components/ui/data-table";
import { PaginationControls } from "@/components/common/PaginationControl";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";

// Interfaces
interface ShippingProfile {
  id: number;
  name: string;
  createdDate: string;
  lastUpdatedDate: string;
  countries: Array<{
    id: number;
    name: string;
    continent: string;
  }>;
}

interface Column {
  header: string;
  accessorKey?: keyof ShippingProfile;
  id?: string;
  cell?: (row: ShippingProfile) => React.ReactNode;
}

const MAX_COUNTRY_BADGES = 7;

const ShippingProfiles = () => {
  const [search, setSearch] = useState("");
  const [continent, setContinent] = useState("");
  const [country, setCountry] = useState("");
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [countries, setCountries] = useState<any[]>([]);

  const fetchProfiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        name: search || null,
        countryId: country || null,
        continent: continent || null,
        pageNumber: currentPage,
        pageSize,
        sortOrder: "desc",
        sortBy: "createdDate",
      };
      const { data } = await ShippingProfileApi.fetchShippingProfiles(payload);
      setProfiles(data.shippingProfileVOS || []);
      setTotalRecords(data.totalRecords || 0);
    } catch (err) {
      setError(err?.message || "Failed to fetch shipping profiles");
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all countries for dropdown
  useEffect(() => {
    async function fetchCountries() {
      try {
        const { data } = await ShippingProfileApi.fetchAllCountries();
        setCountries(data || []);
      } catch {
        setCountries([]);
      }
    }
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line
  }, [search, continent, country, currentPage, pageSize]);

  // Filtered continents and countries for dropdowns
  const allContinents = [
    "NORTH_AMERICA",
    "SOUTH_AMERICA",
    "AUSTRALIA",
    "EUROPE",
    "ASIA",
    "AFRICA",
  ];
  // Flatten all countries from all profiles for dropdown
  const allCountries = Array.from(
    new Set(
      profiles.flatMap((profile) =>
        (profile.countries || []).map((c) => c.name)
      )
    )
  );

  // Define columns for DataTable
  const columns: Column[] = [
    {
      header: "Created Date",
      accessorKey: "createdDate",
      cell: (row) => (
        <div className="font-medium">
          {row.createdDate
            ? format(new Date(row.createdDate), "MMM dd, yyyy, h:mm a")
            : "-"}
        </div>
      ),
    },
    {
      header: "Name",
      accessorKey: "name",
      cell: (row) => (
        <div className="font-medium text-blue-600 hover:underline cursor-pointer">
          {row.name}
        </div>
      ),
    },
    {
      header: "Continent",
      id: "continent",
      cell: (row) => (
        <div className="flex flex-wrap gap-2">
          {Array.from(new Set((row.countries || []).map((c) => c.continent)))
            .filter((cont): cont is string => typeof cont === "string")
            .map((cont) => (
              <Badge
                key={cont}
                variant="secondary"
                className="bg-gray-100 text-gray-700 font-medium px-3 py-1 rounded-md"
              >
                {cont.replace(/_/g, " ")}
              </Badge>
            ))}
        </div>
      ),
    },
    {
      header: "Countries",
      id: "countries",
      cell: (row) => (
        <div className="flex flex-wrap gap-2 items-center">
          {(row.countries || [])
            .slice(0, MAX_COUNTRY_BADGES)
            .filter(
              (country) =>
                country &&
                typeof country.id !== "undefined" &&
                typeof country.name === "string"
            )
            .map((country) => (
              <Badge
                key={String(country.id)}
                variant="secondary"
                className="bg-gray-100 text-gray-700 font-medium px-3 py-1 rounded-md"
              >
                {country.name}
              </Badge>
            ))}
          {(row.countries || []).length > MAX_COUNTRY_BADGES && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost" className="p-1">
                  <MoreHorizontal className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(row.countries || [])
                  .slice(MAX_COUNTRY_BADGES)
                  .filter(
                    (country) =>
                      country &&
                      typeof country.id !== "undefined" &&
                      typeof country.name === "string"
                  )
                  .map((country) => (
                    <DropdownMenuItem key={String(country.id)}>
                      {country.name}
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white min-w-[8rem] p-1"
          >
            <DropdownMenuItem>
              <Link
                to={`/rewardpoint/shipping-profiles/${row.id}`}
                state={{ profile: row }}
              >
                View Details
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Skeleton loading component
  const ShippingProfilesSkeleton = () => (
    <div className="space-y-6">
      {/* Header Skeleton */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-40 mb-2" />
          <Skeleton className="h-4 w-52" />
        </div>
        <Skeleton className="h-10 w-44" />
      </div>

      {/* Filters Skeleton */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1">
          <Skeleton className="h-4 w-24 mb-1" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div>
          <Skeleton className="h-4 w-20 mb-1" />
          <Skeleton className="h-10 w-[180px]" />
        </div>
        <div>
          <Skeleton className="h-4 w-16 mb-1" />
          <Skeleton className="h-10 w-[180px]" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-16" />
          <Skeleton className="h-10 w-16" />
        </div>
      </div>

      {/* Table Skeleton */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <Skeleton className="h-6 w-40" />
        </div>
        <div className="p-6">
          {/* Table Header Skeleton */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-16" />
          </div>
          {/* Table Rows Skeleton */}
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="grid grid-cols-5 gap-4 py-4 border-b border-gray-100 last:border-b-0"
            >
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-32" />
              <div className="flex gap-2">
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-6 w-12 rounded-full" />
                <Skeleton className="h-6 w-14 rounded-full" />
                <Skeleton className="h-6 w-10 rounded-full" />
              </div>
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          ))}
        </div>
      </div>

      {/* Pagination Skeleton */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-16" />
        </div>
        <div className="flex items-center space-x-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <ShippingProfilesSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Shipping Profiles
          </h1>
          <p className="text-gray-500">Manage your shipping profiles</p>
        </div>
        <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
          <Link to="/rewardpoint/shipping-profiles/create">
            <Plus className="h-4 w-4 mr-2" /> Add Shipping Profile
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1">
          <label
            htmlFor="search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Profile Name
          </label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              id="search"
              placeholder="Search profiles..."
              className="pl-9"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(0);
              }}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Continent
          </label>
          <Select
            value={continent}
            onValueChange={(value) => {
              setContinent(value);
              setCurrentPage(0);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select Continent" />
            </SelectTrigger>
            <SelectContent>
              {allContinents.map((c) => (
                <SelectItem key={c} value={c}>
                  {c.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Country
          </label>
          <Select
            value={country}
            onValueChange={(value) => {
              setCountry(value);
              setCurrentPage(0);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent>
              {countries.map((c) => (
                <SelectItem key={String(c.id)} value={c.id.toString()}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={fetchProfiles}
          >
            Apply
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setSearch("");
              setContinent("");
              setCountry("");
              setCurrentPage(0);
            }}
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && <div className="text-red-500">{error}</div>}

      {/* Table */}
      <DataTable
        title="All Shipping Profiles"
        data={profiles}
        columns={columns}
      />

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Rows per page:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setCurrentPage(0);
            }}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PaginationControls
          totalPages={Math.ceil(totalRecords / pageSize)}
          currentPage={currentPage}
          onNextHandler={() =>
            setCurrentPage((p) =>
              Math.min(Math.ceil(totalRecords / pageSize) - 1, p + 1)
            )
          }
          onPrevHandler={() => setCurrentPage((p) => Math.max(0, p - 1))}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  );
};

export default ShippingProfiles;
